import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from 'react';
import {
	Controller,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography } from '@mui/material';
import { AxiosError } from 'axios';

import {
	checkBannedEmail,
	TCheckBannedEmailResultAction,
	useBusinessLayer,
	UserActionTypes,
} from '@mopla/business-logic';
import { AppEnum, ProfileSteps } from '@mopla/data-models';
import { usePersistentCountdownGuard } from '@mopla/utils';

import {
	Input,
	Loader,
	MainWithFooterTemplate,
	MplButton,
	MplErrorModal,
} from '../../../';
import { useProfileManagerContext } from '../context';

import { EmailValidationSchema } from './EditEmailValidation';
import { FormFooter } from './FormFooter';

type TEditEmailFormInputs = {
	email: string;
	emailBanned?: string;
	serverError?: string;
};

type TEditEmailScreenProps = Pick<TEditEmailFormInputs, 'email'> & {
	onAbort: (isDirty: boolean) => void;
	setProfileStep: Dispatch<SetStateAction<ProfileSteps>>;
};

export const EditEmailScreen = ({
	email,
	onAbort,
	setProfileStep,
}: TEditEmailScreenProps) => {
	const { t } = useTranslation(['validation', 'profile']);
	const [loading, setLoading] = useState(false);
	const bl = useBusinessLayer();
	const { countdownGuardValue, reinitCountdownGuard } =
		usePersistentCountdownGuard('PersonalDetails_EditEmail_countdownGuard');

	const formMethods = useForm<TEditEmailFormInputs>({
		defaultValues: {
			email,
		},
		resolver: yupResolver(EmailValidationSchema),
		mode: 'onBlur',
	});

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isSubmitting },
		setError,
		clearErrors,
		resetField,
	} = formMethods;

	const { setPendingNewEmail } = useProfileManagerContext();

	const handleCheckBannedEmail = (email: string) => {
		return new Promise<boolean>((resolve, reject) => {
			setLoading(true);
			bl.dispatch(checkBannedEmail({ email }));

			bl.watchActions({
				types: UserActionTypes.CheckBannedEmailResult,
				callback: (_action) => {
					const action = _action as TCheckBannedEmailResultAction;

					setLoading(false);

					if (action.payload?.error) {
						setError('serverError', {});
						return reject();
					}

					resolve(!action.payload.isValid);
				},
				count: 1,
			});
		});
	};

	const handleBannedEmailOkClick = useCallback(() => {
		clearErrors('emailBanned');
		resetField('email');
	}, [clearErrors, resetField]);

	const handleSkipServerError = useCallback(() => {
		clearErrors('serverError');
	}, [clearErrors]);

	const onSubmit: SubmitHandler<TEditEmailFormInputs> = async (formData) => {
		const isEmailBanned = await handleCheckBannedEmail(formData.email);

		if (isEmailBanned) {
			setError('emailBanned', {});
			return;
		}

		try {
			setLoading(true);
			await bl.api.patch('/api/users/email', {
				email: formData.email,
				origin: AppEnum.PASSENGER,
			});

			setPendingNewEmail(formData.email);

			reinitCountdownGuard();

			setProfileStep(ProfileSteps.PASSENGER_EDIT_EMAIL_SENT);
		} catch (error) {
			if (
				error instanceof AxiosError &&
				error.response?.data.code === 'user_already_exist'
			) {
				setError('email', {
					type: 'custom',
					message: t(`validation:email.exist`),
				});
			}
		} finally {
			setLoading(false);
		}
	};

	const footer = (
		<FormFooter
			isSubmitDisabled={!isDirty || isSubmitting}
			onCancel={() => onAbort(isDirty)}
		/>
	);

	useEffect(() => {
		/** This prevents sending an email once again, when a user goes back and then again changes an email.
		 * Instead, we just redirect the user to the SENT screen */
		if (countdownGuardValue && countdownGuardValue > 0) {
			setProfileStep(ProfileSteps.PASSENGER_EDIT_EMAIL_SENT);
		}
	}, [countdownGuardValue, setProfileStep]);

	if (loading || countdownGuardValue !== false) {
		return <Loader delay={100} />;
	}

	return (
		<>
			<FormProvider {...formMethods}>
				<form
					onSubmit={handleSubmit((data) => onSubmit(data))}
					style={{ height: '100%' }}
				>
					<MainWithFooterTemplate
						content={
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: '1fr',
									gridTemplateRows: '1fr max-content',
									padding: '24px',
								}}
							>
								<Typography variant="body2" mb="16px">
									{t('profile:edit_email.edit_screen.description')}
								</Typography>
								<Controller
									control={control}
									name="email"
									render={({ field }) => (
										<Input
											{...field}
											label={`${t('profile:header.email')}*`}
											helperText={
												errors.email?.message
													? t(`validation:${errors.email?.message}`)
													: ''
											}
											error={!!errors.email}
											type="email"
											autoComplete="username email"
										/>
									)}
								/>
								<Typography variant="caption" color="neutral.80" mt="8px">
									{t('profile:edit_email.edit_screen.caption')}
								</Typography>
							</Box>
						}
						footer={footer}
					/>
				</form>
			</FormProvider>
			<MplErrorModal
				open={!!errors.emailBanned}
				title={t('validation:email.banned.title')}
				content={t('validation:email.banned.text')}
				testId="ChangeEmailFormMplModal-emailBanned"
				buttons={
					<MplButton fullWidth onClick={handleBannedEmailOkClick}>
						{t('validation:email.banned.button')}
					</MplButton>
				}
			/>
			<MplErrorModal
				open={!!errors.serverError}
				testId="ChangeEmailFormMplModal-serverError"
				buttons={
					<MplButton fullWidth onClick={handleSkipServerError}>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
		</>
	);
};
